import axios from "axios";
import Head from "next/head";
import Link from "next/link";

// HTML index of every caliber hub (REV-4 internal linking): one crawlable page that links
// to all /ammo/<type>/<caliber> pages with live in-stock counts. Regenerated hourly (ISR).
const SITE = "https://ammohawk.com";
const BACKEND = (process.env.NEXT_PUBLIC_BACKEND_URL || "https://backend.ammohawk.com").replace(/\/$/, "");
const HUBS_API = `${BACKEND}/api/products/hubs/`;
const REVALIDATE_SECONDS = 60 * 60;
const TYPES = [
  { key: "handgun", label: "Handgun" },
  { key: "rifle", label: "Rifle" },
  { key: "shotgun", label: "Shotgun" },
  { key: "rimfire", label: "Rimfire" },
];

export default function AmmoIndex({ groups, totals }) {
  const title = `Ammo Prices by Caliber: ${totals.calibers} calibers, ${totals.in_stock} loads in stock | AmmoHawk`;
  const description = `Live ammunition prices for ${totals.calibers} calibers, ${totals.in_stock} loads in stock, ranked by price per round and refreshed nightly. Pick a caliber to compare.`;
  const canonical = `${SITE}/ammo`;
  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Ammo by caliber", item: canonical },
    ],
  };
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="AmmoHawk" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbs) }} />
      </Head>
      <main className="hub hubindex">
        <nav className="hub__breadcrumbs" aria-label="Breadcrumb">
          <Link href="/">Home</Link> › <span>Ammo by caliber</span>
        </nav>
        <h1 className="hub__title">Ammo prices by caliber</h1>
        <p className="hub__intro">
          {totals.in_stock} in-stock loads across {totals.calibers} calibers, each page ranked by price per round and
          refreshed nightly. Calibers with nothing in stock today are listed last.
        </p>
        {TYPES.map((t) => {
          const rows = groups[t.key] || [];
          if (!rows.length) return null;
          return (
            <section key={t.key} className="hubindex__group">
              <h2 className="hub__h2">
                <Link href={`/browse-components/${t.key}`}>{t.label} ammo</Link>
              </h2>
              <ul className="hubindex__list">
                {rows.map((c) => (
                  <li key={c.id} className={c.in_stock ? "" : "hubindex__item--oos"}>
                    <Link href={`/ammo/${c.url_type}/${encodeURIComponent(c.url_name)}`}>{c.name}</Link>
                    <span className="hubindex__count">
                      {c.in_stock ? `${c.in_stock} in stock` : `${c.products} listed`}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </main>
    </>
  );
}

export async function getStaticProps() {
  try {
    const { data } = await axios.get(HUBS_API, { timeout: 20000 });
    const groups = {};
    let inStock = 0;
    (Array.isArray(data) ? data : []).forEach((c) => {
      if (!c.url_type || !c.url_name) return;
      (groups[c.url_type] = groups[c.url_type] || []).push(c);
      inStock += c.in_stock || 0;
    });
    Object.values(groups).forEach((rows) => rows.sort((a, b) => (b.in_stock - a.in_stock) || a.name.localeCompare(b.name)));
    const calibers = Object.values(groups).reduce((n, rows) => n + rows.length, 0);
    return { props: { groups, totals: { calibers, in_stock: inStock } }, revalidate: REVALIDATE_SECONDS };
  } catch (error) {
    console.error("hub index unavailable:", error.message);
    return { props: { groups: {}, totals: { calibers: 0, in_stock: 0 } }, revalidate: 300 };
  }
}
